import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Container,
  Eye,
  Loader2,
  Lock,
  MapPin,
  MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { containerLineDriverAPI } from "../../services/api";
import { lockReason } from "../../utils/containerLineQueue";
import { CONTAINER_LINE_STATUS_STYLES } from "../../constants/containerOrderConstants";
import type { ContainerOrderLine } from "../../types/containerOrders";
import {
  activeStopsOf,
  completedStepCount,
  lineSteps,
} from "../../utils/containerLineSteps";
import { missingLineStartRequirements } from "../../utils/containerLineStart";
import { TruckHeader } from "../../components/devices/TruckHeader";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import type { ContainerMessageThreadEvent } from "../../types/containerMessages";
import { hasDeviceSession, updatePairedTruck } from "../../utils/deviceSession";

/**
 * How far through its run a container is, and how long the run is.
 *
 * Both numbers come off the container rather than off a constant, because the
 * run is the container's own: one box in a batch may stop at a weighbridge, and
 * another may not be collecting from a terminal at all. "3/6" against a route
 * that has eight steps is not a progress figure, it is a wrong one.
 */
export function stepProgress(line: ContainerOrderLine): {
  done: number;
  total: number;
} {
  return { done: completedStepCount(line), total: lineSteps(line).length };
}

type Tab = "active" | "completed";

const TABS: Tab[] = ["active", "completed"];

/**
 * How many containers arrive at a time.
 *
 * Twenty, and the number is chosen against the screen rather than against the
 * network. A card is roughly 140px tall on a phone, so twenty is about three
 * screenfuls — enough that an ordinary day arrives in one request and nobody
 * ever sees the loader, and few enough that a truck with eight months of
 * completed runs behind it opens its history in the same time.
 *
 * The server caps anything larger (`DRIVER_PAGE_MAX`), so this is a request and
 * not a promise; the client reads `hasMore` rather than comparing lengths
 * against it.
 */
const PAGE_SIZE = 20;

/**
 * One tab's worth of list, as far as it has been scrolled.
 *
 * `total` is the whole scope's count and not `lines.length` — it is what the
 * number beside the tab name shows, and a count that grew as the driver
 * scrolled would be telling them their workload was increasing.
 */
interface TabState {
  lines: ContainerOrderLine[];
  /** The highest page fetched so far. */
  page: number;
  hasMore: boolean;
  total: number;
}

/**
 * The containers on this truck — the screen a shift starts at.
 *
 * The list is the *truck's*, not the signed-in driver's: containers are
 * dispatched to a vehicle, and the server scopes this to whichever truck the
 * device is paired to. Two drivers across a shift change see the same list,
 * which is the point — the box did not change hands, the wheel did.
 *
 * Two tabs, because a driver needs both halves: Active is the work owing, and
 * Completed is the truck's recent record — which box, which terminal, what time
 * it was returned. Each tab is a separate request (the server filters by status)
 * and is cached once fetched, so flipping back and forth does not re-hit the
 * network.
 */
export default function DriverContainersListPage() {
  const { t } = useTranslation("containerOrders");
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Whether this is a truck session, not merely a paired tablet.
   *
   * The route already refuses anything else, so this only decides the header:
   * with a truck session the plate and the shift control stand in for the back
   * arrow, because there is nowhere behind this page to go — the page *is* the
   * shift.
   *
   * Read once per render rather than held in state: it cannot change while this
   * page is open, and a session that ends is redirected away by the interceptor
   * rather than re-rendering here.
   */
  const onDevice = hasDeviceSession();

  // The execution screen sends back the tab its container belongs to, so
  // reviewing a finished run returns here rather than to the to-do list.
  const [tab, setTab] = useState<Tab>(() =>
    (location.state as { tab?: Tab } | null)?.tab === "completed"
      ? "completed"
      : "active",
  );
  const [byTab, setByTab] = useState<Partial<Record<Tab, TabState>>>({});
  const [loading, setLoading] = useState(true);
  /**
   * A page after the first is on its way.
   *
   * Separate from `loading`, because the two mean opposite things to the
   * driver: `loading` replaces the list with a spinner, and this one leaves
   * every card where it is and puts a spinner under them. Sharing one flag
   * would make a scroll to the bottom blank the list the driver was reading.
   *
   * Keyed by nothing — only one request is ever in flight, because the sentinel
   * below will not fire again while this is set.
   */
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = byTab[tab];
  const lines = state?.lines;

  /**
   * The cancellation token the scroll-driven fetches share.
   *
   * Page one has its own, created and torn down by the effect that watches the
   * tab. The pages after it are started by an observer rather than by an
   * effect, so there is no cleanup function to put the token in — it lives
   * here and is replaced whenever the tab changes, which is what stops a page
   * of completed runs reporting its failure over the active list the driver
   * has since switched to.
   */
  const moreSignal = useRef({ active: true });

  useEffect(() => {
    const signal = moreSignal.current;
    return () => {
      signal.active = false;
      moreSignal.current = { active: true };
      /*
       * And the flag with it. `load` only clears `loadingMore` when its own
       * signal is still live — which is right, or a cancelled request would
       * switch off the spinner belonging to the tab the driver has moved to.
       * The consequence is that cancelling a page fetch leaves the flag set,
       * and `loadMore` refuses to run while it is: scrolling the list would
       * never load another page again for the life of the screen. Clearing it
       * here is the other half of the cancellation.
       */
      setLoadingMore(false);
    };
  }, [tab]);

  const load = useCallback(
    async (scope: Tab, page: number, signal: { active: boolean }) => {
      const first = page === 1;
      setError(null);
      if (first) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await containerLineDriverAPI.getMine(scope, {
          page,
          limit: PAGE_SIZE,
        });
        if (signal.active) {
          const lines = (res.data.data.lines ?? []) as ContainerOrderLine[];
          const hasMore = Boolean(res.data.data.hasMore);
          const total = res.data.data.total ?? lines.length;

          setByTab((prev) => {
            const held = prev[scope];

            /*
             * A page after the first is appended, and appended *defensively*.
             *
             * Two things can put a container in both the held list and the
             * page that follows it. The office finishes a run while the driver
             * is scrolling, which shortens the active scope and shifts every
             * later row back by one; or the driver reaches the bottom twice in
             * the same instant. Either way an id arriving twice would mount
             * two cards with the same key — React's own warning, and a list
             * that shows the same box in two places. Deduplicating on the way
             * in costs one Set per page and makes the question moot.
             */
            if (!first && held) {
              const seen = new Set(held.lines.map((line) => line._id));
              return {
                ...prev,
                [scope]: {
                  lines: [
                    ...held.lines,
                    ...lines.filter((line) => !seen.has(line._id)),
                  ],
                  page,
                  hasMore,
                  total,
                },
              };
            }

            return { ...prev, [scope]: { lines, page, hasMore, total } };
          });

          /**
           * The plate in the header, kept current for free.
           *
           * Every container on this list belongs to the truck this device is
           * paired to, and the server populates each one's `vehicleId` with the
           * live registration — so the response already contains the answer to
           * "has the office renamed this truck since we paired?". Reading it
           * here costs nothing and is what makes an edited plate show up on the
           * driver's screen during a shift, rather than only after the next PIN
           * sign-in.
           *
           * Only the plate and display name: the populate selects nothing
           * else, which is why `updatePairedTruck` merges rather than replaces.
           * A display name absent from the populate is one the office cleared.
           */
          const vehicle = lines.find(
            (line: ContainerOrderLine) =>
              typeof line.vehicleId === "object" && line.vehicleId,
          )?.vehicleId;

          if (vehicle && typeof vehicle === "object") {
            updatePairedTruck({
              registrationNumber: vehicle.registrationNumber,
              displayName: vehicle.displayName ?? null,
            });
          }
        }
      } catch (e: any) {
        if (signal.active) {
          /*
           * A failed *further* page leaves the list standing and says so
           * underneath it. Replacing three screenfuls the driver is reading
           * with a one-line error because the twenty-first container did not
           * arrive would be throwing away the part that worked.
           */
          setError(e.response?.data?.message || t("driver.loadFailed"));
        }
      } finally {
        if (signal.active) {
          if (first) setLoading(false);
          else setLoadingMore(false);
        }
      }
    },
    [t],
  );

  /**
   * Fetch the next page, if there is one and nothing is already fetching it.
   *
   * Guarded here rather than only at the sentinel, because the sentinel is not
   * the only thing that can ask twice: an IntersectionObserver fires again on
   * every resize and on the layout shift that appending a page itself causes,
   * and a phone rotating at the bottom of a list would otherwise queue two
   * requests for page four.
   */
  const loadMore = useCallback(() => {
    const held = byTab[tab];
    if (!held || !held.hasMore || loading || loadingMore) return;
    load(tab, held.page + 1, moreSignal.current);
  }, [byTab, tab, loading, loadingMore, load]);

  /**
   * Unread badges on this truck's containers, kept live.
   *
   * The counters ride on the containers themselves, so the list arrives with
   * them already correct; this is only what keeps them correct while the list
   * sits open. The event comes from the truck's own socket room and carries a
   * counter and a preview — never a message — so a list of twelve containers
   * costs nothing to keep current no matter how much is being said on any of
   * them. The words are only ever delivered into the room of the container
   * whose thread is actually open. See backend/socket/emitters.js.
   *
   * Patched in place rather than triggering a refetch: a driver watching this
   * screen would otherwise pay a full list request every time the office typed
   * a sentence.
   */
  /**
   * Dispatch changed what this truck is carrying.
   *
   * The board sends a container to a truck, pulls one back, or reorders the
   * day — and until this existed none of it reached the cab. The container was
   * correctly on the driver's list the *next* time the page was opened, which
   * is not what "send to truck" means to the dispatcher who pressed it: they
   * expect the row to appear, and a driver waiting on a job they were told was
   * coming had no way to make it show up but to leave the screen and come back.
   *
   * Refetches rather than patches, for the same reason the office board does:
   * the signal deliberately carries no container (see `emitTruckPlanChanged`),
   * because a reordered day moves several rows at once and a list reassembled
   * from the server cannot drift the way an incrementally-patched one can.
   *
   * Both tabs are dropped, not just the visible one. The cached Completed tab
   * is as capable of being wrong as the Active one — a container pulled back
   * mid-list changes neither, but a run finished on another device changes
   * both — and refetching one while silently trusting the other is how a driver
   * ends up with two tabs that disagree.
   *
   * Addressed to the truck rather than to the driver: the server emits into the
   * cab's room, which is the same routing the containers themselves use, so a
   * shift change mid-afternoon needs no re-subscribing.
   */
  useSocketEvent("truck-plan:changed", () => {
    /*
     * Dropping the cache is the whole of the refetch.
     *
     * The effect below already watches `byTab` and fetches whichever tab has no
     * entry — with a cancellation signal, so a response cannot land on a tab the
     * driver has since left. Calling `load` here as well would fire a second,
     * uncancellable request for the same data.
     */
    setByTab({});
  });

  useSocketEvent<ContainerMessageThreadEvent>(
    "containerMessage:thread",
    (payload) => {
      if (!payload?.lineId) return;

      setByTab((prev) => {
        let touched = false;

        const next = Object.fromEntries(
          Object.entries(prev).map(([scope, held]) => {
            if (!held?.lines.some((line) => line._id === payload.lineId)) {
              return [scope, held];
            }
            touched = true;
            // Only the rows change: the page reached, the total and `hasMore`
            // are untouched by somebody typing a message.
            return [
              scope,
              {
                ...held,
                lines: held.lines.map((line) =>
                  line._id === payload.lineId
                    ? { ...line, messaging: payload.messaging ?? {} }
                    : line,
                ),
              },
            ];
          }),
        );

        // A container this list has never held — a scope that was never
        // fetched, one that has since finished, or one still further down a
        // list nobody has scrolled to. Returning `prev` keeps the identity, so
        // nothing re-renders.
        return touched ? next : prev;
      });
    },
  );

  useEffect(() => {
    // Already fetched — show it without a spinner or a second request. However
    // far it has been scrolled: flipping to Completed and back must not throw
    // away four pages of active work and re-fetch the first.
    if (byTab[tab]) {
      setLoading(false);
      setError(null);
      return;
    }

    // The driver may switch tabs or navigate away mid-request; without this the
    // response would land on a tab that is no longer showing.
    const signal = { active: true };
    load(tab, 1, signal);
    return () => {
      signal.active = false;
    };
  }, [tab, byTab, load]);

  /**
   * The bottom of the list, as something the browser can tell us about.
   *
   * An IntersectionObserver on a sentinel rather than a scroll listener: the
   * list is the page's own scroll (the cards are not in a scroll container), so
   * a listener would mean measuring `scrollHeight` on every frame of every
   * flick — on a phone, in a cab, while the screen is also being asked to
   * render twenty cards. The observer fires once when the marker appears and
   * costs nothing in between.
   *
   * `rootMargin` pulls the trigger 400px *before* the marker is visible, which
   * is the difference between a list that loads more and a list that stutters:
   * the next page is already arriving by the time the driver's thumb reaches
   * the last card.
   */
  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        /*
         * Not while the last attempt is still showing as failed. `hasMore` is
         * deliberately left true by a failure — the page is still out there —
         * so without this the observer would retry on every layout shift and
         * a cab with no signal would be a request loop. The retry button
         * below is the way out, and pressing it clears the error.
         */
        if (error) return;
        loadMore();
      },
      { rootMargin: "400px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, error]);

  // Fills the app shell, which is already sized to the visible screen —
  // `100vh` was taller than that on iOS and pushed the end of a long queue
  // below the fold. No bottom bar here, so this page owns the home indicator
  // gap itself.
  return (
    <div className="driver-ui pt-safe min-h-full pb-safe bg-[#FAFAF8]">
      {/* On a truck's screen the plate and the shift control replace the back
          arrow: there is nowhere behind this page to go, because this page *is*
          the shift. On a phone the arrow stays and the bar renders nothing. */}
      <TruckHeader />

      <div className="border-b border-[#ECEAE4] bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          {!onDevice && (
            <button
              type="button"
              onClick={() => navigate("/driver-home")}
              aria-label={t("driver.back")}
              className="rounded-lg p-2 hover:bg-[#F5F4F0]"
            >
              <ArrowLeft className="h-5 w-5 text-[#5A6460]" />
            </button>
          )}
          <h1 className="text-lg font-semibold text-[#0B0F0E]">
            {t("driver.home.title")}
          </h1>
        </div>

        <div
          role="tablist"
          aria-label={t("driver.home.title")}
          className="mx-auto flex max-w-3xl gap-1 px-4"
        >
          {TABS.map((key) => {
            const selected = tab === key;
            // The whole scope's count, not how much of it has been scrolled
            // into view — a number that climbed as the driver scrolled would
            // read as the workload growing.
            const count = byTab[key]?.total;

            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setTab(key)}
                className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  selected
                    ? "border-[#C97A3C] text-[#0B0F0E]"
                    : "border-transparent text-[#8A9590] hover:text-[#5A6460]"
                }`}
              >
                {t(`driver.tabs.${key}`)}
                {count !== undefined && (
                  <span className="ml-1.5 text-xs text-[#8A9590]">{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-[#8A9590]">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("linesBlock.loading")}
          </div>
        ) : error && !lines?.length ? (
          /* The error takes the screen only when it is the whole of what
             happened. A page that failed to arrive underneath three screenfuls
             of cards is reported below them instead (see the retry note under
             the list) — replacing a list the driver is reading with a one-line
             failure would be throwing away the part that worked. */
          <p className="py-16 text-center text-sm text-[#E24B4A]">{error}</p>
        ) : !lines?.length ? (
          <p className="py-16 text-center text-sm text-[#8A9590]">
            {t(`driver.empty.${tab}`)}
          </p>
        ) : (
          <ul className="space-y-3">
            {lines.map((line) => {
              const { done, total } = stepProgress(line);
              const next = done < total ? lineSteps(line)[done] : null;
              const finished = line.status === "completed";

              /**
               * Whether this box is the driver's to work yet.
               *
               * One rule, shared with the execution screen, so a card that
               * refuses to open and the banner on the screen behind a
               * bookmarked link cannot disagree about which container is in the
               * way. Asked only of the active tab: the completed list is a
               * record a driver looks things up in, and locking history would
               * be refusing to show somebody their own afternoon.
               *
               * Passed the list as it is drawn, which is the order the server
               * sorted — see `lockReason`.
               */
              const locked =
                tab === "active" ? lockReason(line, lines) : null;

              // Only ever blocking before the first step: a run under way is
              // never held up by paperwork edited behind the driver's back.
              // Suppressed on a locked card, where it would be a second
              // explanation of why nothing can be done and the less useful one
              // — the paperwork is not what is in the way, the box in front is.
              const missing =
                done === 0 && !locked ? missingLineStartRequirements(line) : [];

              return (
                <li key={line._id}>
                  <button
                    type="button"
                    /*
                     * `disabled` and not merely a guard inside the handler: it
                     * takes the card out of the tab order and off the
                     * accessibility tree's list of things that can be pressed,
                     * so a locked container is not reachable by keyboard, by
                     * screen reader, or by a thumb landing on it while the
                     * truck moves. A handler that silently did nothing would
                     * read as a card that had stopped working.
                     */
                    disabled={!!locked}
                    onClick={() => navigate(`/driver/containers/${line._id}`)}
                    className={`group w-full rounded-2xl border p-4 text-left transition-all ${
                      locked
                        ? /*
                           * Shown, and shown as unavailable. The driver has to
                           * be able to read what is coming — which box, which
                           * terminals, in what order — while being left in no
                           * doubt that it is not today's next job. So: the
                           * dashed edge the platform already uses for "a slot
                           * rather than a thing", a flat grey ground instead of
                           * the white a live card sits on, no shadow (a live
                           * card is lifted), and the content faded. Four
                           * signals, because one of them is colour and colour
                           * alone is not a signal on a sunlit windscreen.
                           */
                          "cursor-not-allowed border-dashed border-[#D8D6CE] bg-[#F5F4F0] opacity-[0.72]"
                        : "border-[#ECEAE4] bg-white shadow-sm hover:border-[#C97A3C] hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        {/*
                          Where dispatch put this in the day's order.
                          The list is already sorted by it — the server sorts on
                          the same two fields — but a sorted list only tells you
                          the order while you can see the whole of it, and a
                          driver checking one row against the board over the
                          phone needs the number itself. Shown only on the
                          active tab and only when the board has actually placed
                          the container: a finished run's slot is history, and an
                          unplanned one has no number to show.
                        */}
                        {!finished && line.planning?.slot ? (
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2F7A5B] text-[11px] font-bold text-white">
                            {line.planning.slot}
                          </span>
                        ) : (
                          <Container
                            className={`h-5 w-5 shrink-0 ${
                              finished ? "text-[#8A9590]" : "text-[#2F7A5B]"
                            }`}
                          />
                        )}
                        <span className="truncate font-semibold text-[#0B0F0E]">
                          {line.lineNumber}
                        </span>
                        {line.containerNumber && (
                          <span className="truncate text-sm text-[#8A9590]">
                            {line.containerNumber}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {/* Something is waiting on this container.
                            The count, not the message: the words are fetched
                            only when the thread is opened, so a list of a
                            dozen containers costs one request and not a
                            dozen. */}
                        {(line.messaging?.unreadForDriver ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#C0392B] px-1.5 py-0.5 text-[11px] font-semibold text-white">
                            <MessageCircle className="h-3 w-3" />
                            {(line.messaging?.unreadForDriver ?? 0) > 9
                              ? "9+"
                              : line.messaging?.unreadForDriver}
                          </span>
                        )}
                        {/* The same badge the office sees, on the driver's own
                            list. Worth its space now that it names where the
                            box is rather than only that it is somewhere: a
                            driver picking up a run mid-shift reads it instead
                            of opening each card to find out. */}
                        <span
                          className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            CONTAINER_LINE_STATUS_STYLES[line.status] ??
                            CONTAINER_LINE_STATUS_STYLES.planned
                          }`}
                        >
                          {t(`linesBlock.statuses.${line.status}`)}
                        </span>
                        {/* What pressing this card would do. A padlock where
                            the chevron goes is the one place the affordance
                            itself says no — every other treatment on a locked
                            card is a shade, and a shade can be lost to
                            sunlight. */}
                        {locked ? (
                          <Lock
                            aria-label={t("driver.queueLock.badge")}
                            className="h-5 w-5 shrink-0 text-[#A09E95]"
                          />
                        ) : finished ? (
                          <Eye
                            aria-label={t("linesBlock.viewLine")}
                            className="h-5 w-5 shrink-0 text-[#C7CCC9] group-hover:text-[#C97A3C]"
                          />
                        ) : (
                          <ChevronRight className="h-5 w-5 shrink-0 text-[#C7CCC9] group-hover:text-[#C97A3C]" />
                        )}
                      </div>
                    </div>

                    {/* The run as the driver is actually going to make it.
                        Read off the route rather than off the three anchors, so
                        a terminal that is not being used does not appear as a
                        place this driver is expected to go, and a weighbridge
                        that is does. Truncated to one line, which is what the
                        card has room for — the whole thing is one tap away. */}
                    <div className="mt-2 flex items-center gap-1.5 text-sm text-[#5A6460]">
                      <MapPin className="h-4 w-4 shrink-0 text-[#8A9590]" />
                      <span className="truncate">
                        {activeStopsOf(line)
                          .map(
                            (stop) =>
                              stop.companyName?.trim() ||
                              stop.label?.trim() ||
                              "—",
                          )
                          .join(" → ")}
                      </span>
                    </div>

                    {finished ? (
                      <div className="mt-2 flex items-center gap-1.5 text-[13px] text-[#2F7A5B]">
                        <Check className="h-4 w-4 shrink-0" />
                        {line.completedAt
                          ? t("driver.completedOn", {
                              date: new Date(
                                line.completedAt,
                              ).toLocaleString(),
                            })
                          : t("linesBlock.statuses.completed")}
                      </div>
                    ) : (
                      <div className="mt-2 text-[13px] text-[#8A9590]">
                        {next
                          ? `${done}/${total} · ${t("driver.next")}: ${t(`linesBlock.steps.${next.step}`)}`
                          : t("linesBlock.statuses.completed")}
                      </div>
                    )}

                    {/* Which box is in the way, by name.
                        The whole difference between a lock a driver can act on
                        and one that reads as a broken app: "finish FC-1043/2
                        first" names the card at the top of the list they are
                        already looking at, where a padlock on its own names
                        nothing and invites a call to the office. */}
                    {locked && (
                      <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[#ECEAE4] px-2.5 py-1.5 text-[12px] text-[#5A6460]">
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          {t("driver.queueLock.card", {
                            container:
                              locked.blockedBy.lineNumber ||
                              locked.blockedBy.containerNumber ||
                              "",
                          })}
                        </span>
                      </div>
                    )}

                    {missing.length > 0 && (
                      <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-[#FCEEE2] px-2.5 py-1.5 text-[12px] text-[#8A5A28]">
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          {t("driver.blocked.short", {
                            fields: missing
                              .map((key) => t(`driver.requirements.${key}`))
                              .join(", "),
                          })}
                        </span>
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* ── The bottom of the list ──────────────────────────────────────
            Outside the three-way branch above on purpose: the marker has to be
            mounted for the observer in the effect to have anything to watch,
            and a marker that only existed once there were already cards would
            never see the first load. It occupies no space and says nothing
            unless there is something to say.

            `aria-hidden` on the marker itself — it is a scroll trigger, not
            content — while the spinner beside it is announced, because "more is
            arriving" is the one thing a driver who cannot see the bottom of the
            list needs told. */}
        <div ref={sentinel} aria-hidden className="h-px w-full" />

        {loadingMore && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 py-6 text-sm text-[#8A9590]"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("linesBlock.loading")}
          </div>
        )}

        {/* A page that did not arrive, with the cards above it left alone.
            Tappable rather than automatic: the sentinel is already past, so
            without a button the driver would have to scroll up and back down
            to provoke another attempt — and an automatic retry against a cab
            with no signal is a request loop nobody asked for. */}
        {error && !loadingMore && !!lines?.length && state?.hasMore && (
          <div className="py-6 text-center">
            <p className="text-sm text-[#E24B4A]">{error}</p>
            <button
              type="button"
              onClick={loadMore}
              className="mt-2 rounded-lg border border-[#ECEAE4] bg-white px-4 py-2 text-sm font-medium text-[#5A6460] active:bg-[#F5F4F0]"
            >
              {t("driver.listRetry")}
            </button>
          </div>
        )}

        {/* How long the list actually is, once the driver has reached the end
            of it. Only worth saying when there was more than one page: on a
            four-container day the cards are the whole answer, and a footer
            counting them would be the screen explaining itself for no reason. */}
        {!loading &&
          !loadingMore &&
          state &&
          !state.hasMore &&
          state.total > PAGE_SIZE && (
            <p className="py-6 text-center text-xs text-[#A09E95]">
              {t("driver.listEnd", { count: state.total })}
            </p>
          )}
      </div>
    </div>
  );
}
